import React, { useEffect } from 'react';
import {
  Backdrop,
  Box,
  Card,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CommonLayout from 'layouts/DashboardLayout';
import AngleRightIcon from 'common/customIcons/angle_right';
import MDButton from 'components/MDButton';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { getBrandDetails } from '../../../redux/slices/hyperwolfBrand';
import StatusText from 'common/component/StatusText';

const BASE_PATH = '/hyperwolf/brands';

const richTextSx = {
  '& p': { mb: 1 },
  '& ul, & ol': { pl: 2.5, mb: 1 },
  '& h1, & h2, & h3': { mt: 1.5, mb: 0.5 },
};

export default function BrandDetails() {
  const { brandId = '' } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { brandDetails = null, isFetchingDetails = false } = useSelector(
    (state) => state.hyperwolfBrand
  );

  useEffect(() => {
    if (brandId) {
      dispatch(getBrandDetails(brandId));
    }
  }, [dispatch, brandId]);

  const normalizedStatus =
    brandDetails?.status === true || brandDetails?.status === 'active'
      ? 'active'
      : brandDetails?.status === false || brandDetails?.status === 'inactive'
      ? 'inactive'
      : '';

  const formatValue = (value, emptyFallback = '-') => {
    if (value === null || value === undefined) return emptyFallback;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (!normalized || normalized === '-' || normalized === '--' || normalized === 'null' || normalized === 'undefined') {
        return emptyFallback;
      }
      return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (Array.isArray(value)) {
      const items = value.map((v) => formatValue(v, '')).filter(Boolean);
      return items.length ? items.join(', ') : emptyFallback;
    }
    if (typeof value === 'object') {
      // Common API shape fallback for display-only values.
      const preferred =
        value?.name ||
        value?.title ||
        value?.label ||
        value?.vendorName ||
        value?.brandName ||
        value?.slug ||
        value?._id;
      if (preferred) return String(preferred);
      try {
        return JSON.stringify(value);
      } catch {
        return emptyFallback;
      }
    }
    return emptyFallback;
  };

  const safeHtml = (value) => {
    if (typeof value === 'string' && value.trim()) return value;
    return '<p>-</p>';
  };

  const safeImageSrc = (value) => {
    if (typeof value === 'string' && value.trim()) return value;
    return '';
  };

  const formattedPhoneRaw = formatValue(brandDetails?.salesRepresentativePhone);
  const formattedPhone = formattedPhoneRaw !== '-' ? `+1 ${formattedPhoneRaw}` : '-';

  const faqs = Array.isArray(brandDetails?.faqs) ? brandDetails.faqs : [];
  const vendorNames = Array.isArray(brandDetails?.vendorList)
    ? brandDetails.vendorList
        .map((vendor) => {
          if (typeof vendor === 'string') return vendor;
          return vendor?.vendorName || vendor?.name || vendor?.vendorId || '';
        })
        .filter(Boolean)
    : [];
  const idForEdit = brandDetails?._id || brandId;
  const brandIdForEdit = brandDetails?.brandId || brandId;
  const brandBannerImage = safeImageSrc(brandDetails?.brandImage ?? brandDetails?.banner ?? '');
  const brandLogoImage = safeImageSrc(brandDetails?.logo);

  return (
    <CommonLayout>
      <Box p={{ xs: 2, sm: 3, lg: 4 }} pb={{ lg: 2 }} position="relative" bgcolor="grey.50" minHeight="100%">
        <Backdrop
          open={isFetchingDetails}
          sx={{
            color: '#fff',
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
        >
          <CircularProgress color="inherit" />
        </Backdrop>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          mb={2}
          gap={2}
        >
          <Box display="flex" alignItems="center" color="text.main" lineHeight={1}>
            <IconButton onClick={() => navigate(BASE_PATH)}>
              <AngleRightIcon fontSize="14px" />
            </IconButton>
            <Typography variant="h6" fontWeight="semibold" ml={0.75}>
              Brands
            </Typography>
          </Box>
          <Stack direction="row" alignItems="center" gap={2}>
            {/* {normalizedStatus && <StatusText status={normalizedStatus} />} */}
            {idForEdit && (
              <MDButton
                type="button"
                variant="outlined"
                color="primary"
                onClick={() => {
                  navigate(
                    `${BASE_PATH}/edit?id=${encodeURIComponent(idForEdit)}&brandId=${encodeURIComponent(brandIdForEdit)}`
                  );
                }}
              >
                Edit
              </MDButton>
            )}
          </Stack>
        </Stack>

        {!brandDetails && !isFetchingDetails && (
          <Typography color="text.secondary">Brand not found.</Typography>
        )}

        {brandDetails && (
          <>
            {/* Brand Info - layout exactly as design */}
            <Card
              sx={{
                p: { xs: 2, md: 3 },
                mb: 4,
                borderRadius: 2.5,
                boxShadow: '0 0 1px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.06)',
                bgcolor: '#fff',
              }}
            >
              {/* Header row: Brand Info (left) | Status (right) */}
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={3}
              >
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  color="black.main"
                >
                  Brand Info
                </Typography>
                {normalizedStatus && (
                  <StatusText status={normalizedStatus} />
                )}
              </Stack>

              {/* Brand Logo and Brand Banner - same size square boxes, side by side */}
              <Grid container spacing={3} mb={3}>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    fontWeight="600"
                    color="text.secondary"
                    mb={1}
                  >
                    Brand Logo
                  </Typography>
                  {brandLogoImage ? (
                    <Box
                      sx={{
                        width: 160,
                        height: 160,
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: 'grey.300',
                        bgcolor: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 1,
                      }}
                    >
                      <Box
                        component="img"
                        src={brandLogoImage}
                        alt="Brand logo"
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                        }}
                      />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        width: 160,
                        height: 160,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'grey.300',
                        bgcolor: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No logo
                      </Typography>
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    fontWeight="600"
                    color="text.secondary"
                    mb={1}
                  >
                    Brand Banner
                  </Typography>
                  {brandBannerImage ? (
                    <Box
                      sx={{
                        width: 160,
                        height: 160,
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: 'grey.300',
                        bgcolor: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 1,
                      }}
                    >
                      <Box
                        component="img"
                        src={brandBannerImage}
                        alt="Brand banner"
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                        }}
                      />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        width: 160,
                        height: 160,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'grey.300',
                        bgcolor: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No banner
                      </Typography>
                    </Box>
                  )}
                </Grid>
              </Grid>

              {/* Attributes: 4 columns per row, label (dark grey) above value (bold black) */}
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Brand Name
                  </Typography>
                  <Typography variant="body1" fontWeight="small" color="text.primary">
                    {formatValue(brandDetails.brandName)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Brand Heading (H1)
                  </Typography>
                  <Typography variant="body1" fontWeight="small" color="text.primary">
                    {formatValue(brandDetails.heading)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Brand Slug
                  </Typography>
                  <Typography variant="body1" fontWeight="small" color="text.primary">
                    {formatValue(brandDetails.slug)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Brand Sales Representative Name
                  </Typography>
                  <Typography variant="body1" fontWeight="small" color="text.primary">
                    {formatValue(brandDetails.salesRepresentativeName)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Brand Sales Representative Phone Number
                  </Typography>
                  <Typography variant="body1" fontWeight="small" color="text.primary">
                    {formattedPhone}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Brand Sales Representative Email
                  </Typography>
                  <Typography variant="body1" fontWeight="small" color="text.primary">
                    {formatValue(brandDetails.salesRepresentativeEmail)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Brand Website URL
                  </Typography>
                  <Typography variant="body1" fontWeight="small" color="text.primary">
                    {formatValue(brandDetails.websiteUrl)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Brand Billing Email
                  </Typography>
                  <Typography variant="body1" fontWeight="small" color="text.primary">
                    {formatValue(brandDetails.billingEmail)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Vendors
                  </Typography>
                  <Typography variant="body1" fontWeight="small" color="text.primary">
                    {vendorNames.length ? vendorNames.join(', ') : '-'}
                  </Typography>
                </Grid>
              </Grid>

              {/* Short Description */}
              <Typography
                variant="body2"
                fontWeight="600"
                color="secondary"
                mt={4}
                mb={1}
              >
                Short Description
              </Typography>
              <Box
                sx={{ ...richTextSx, color: 'text.primary' }}
                dangerouslySetInnerHTML={{
                  __html: safeHtml(brandDetails.shortDescription),
                }}
              />

              {/* Bottom Description */}
              <Typography
                variant="body2"
                fontWeight="600"
                color="secondary"
                mt={4}
                mb={1}
              >
                Bottom Description
              </Typography>
              <Box
                sx={{ ...richTextSx, color: 'text.primary' }}
                dangerouslySetInnerHTML={{
                  __html: safeHtml(brandDetails.bottomDescription),
                }}
              />
            </Card>

            {/* FAQs - exact design: white card, bold header, each FAQ = bold question + regular answer, no Question/Answer labels */}
            <Card
              sx={{
                p: { xs: 2, md: 3 },
                mb: 4,
                borderRadius: 2.5,
                boxShadow: '0 0 1px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.06)',
                bgcolor: '#fff',
              }}
            >
              <Typography
                variant="h5"
                fontWeight="bold"
                color="text.primary"
                mb={3}
              >
                FAQs
              </Typography>
              {faqs.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No FAQs
                </Typography>
              ) : (
                <Stack spacing={3}>
                  {faqs.map((faq, index) => (
                    <Box key={index}>
                      <Box
                        component="div"
                        sx={{
                          fontWeight: 700,
                          color: 'text.primary',
                          fontSize: '1rem',
                          mb: 1,
                          ...richTextSx,
                        }}
                        dangerouslySetInnerHTML={{
                          __html: safeHtml(faq.question),
                        }}
                      />
                      <Box
                        component="div"
                        sx={{
                          fontWeight: 400,
                          color: 'text.secondary',
                          fontSize: '0.875rem',
                          ...richTextSx,
                        }}
                        dangerouslySetInnerHTML={{
                          __html: safeHtml(faq.answer),
                        }}
                      />
                    </Box>
                  ))}
                </Stack>
              )}
            </Card>

            {/* Meta Properties - exact design: white card, label (small regular) above value (larger bold) */}
            <Card
              sx={{
                p: { xs: 2, md: 3 },
                mb: 4,
                borderRadius: 2.5,
                boxShadow: '0 0 1px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.06)',
                bgcolor: '#fff',
              }}
            >
              <Typography
                variant="h5"
                fontWeight="bold"
                color="text.primary"
                mb={3}
              >
                Meta Properties
              </Typography>
              <Box mb={3}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ fontWeight: 400, fontSize: '0.75rem' }}
                >
                  Meta Title
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight="medium"
                  color="text.primary"
                  display="block"
                  sx={{ mt: 0.5 }}
                >
                  {formatValue(brandDetails.metaTitle)}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ fontWeight: 400, fontSize: '0.75rem' }}
                >
                  Description
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight="medium"
                  color="text.primary"
                  display="block"
                  sx={{ mt: 0.5 }}
                >
                  {formatValue(brandDetails.metaDescription)}
                </Typography>
              </Box>
            </Card>
          </>
        )}
      </Box>
    </CommonLayout>
  );
}
